import { computeBrand } from "../brand";
import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { Menu, LogOut, MoreHorizontal, X } from "lucide-react";
import clsx from "clsx";
import { initials } from "../format";
import { useT } from "../i18n";
import { ViewOnly } from "./Misc";

export interface NavItem { to: string; label: string; icon?: ReactNode; end?: boolean; badge?: number; badgeTone?: "warn" | "brand" }
export interface NavSection { title?: string; items: NavItem[] }
export type ShellVariant = "admin" | "portal" | "hosting";

function CountBadge({ n, tone = "brand", dark }: { n?: number; tone?: "warn" | "brand"; dark?: boolean }) {
  if (!n) return null;
  return <span className={clsx("ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold", tone === "warn" ? "iga-tone-warn" : dark ? "bg-provider-accent text-white" : "bg-brand-soft text-brand-text")}>{n > 99 ? "99+" : n}</span>;
}

/** Logo tile: the tenant logo if given, else initials on the brand colour (provider accent for hosting). */
export function LogoTile({ name, logoUrl, hosting, size = 28 }: { name: string; logoUrl?: string | null; hosting?: boolean; size?: number }) {
  const r = Math.round(size / 4);
  if (logoUrl) return <img src={logoUrl} alt="" width={size} height={size} className="flex-none bg-white object-contain" style={{ borderRadius: r }} />;
  return <span aria-hidden="true" className={clsx("flex flex-none items-center justify-center font-bold text-white", hosting ? "bg-provider-accent" : "bg-brand text-on-brand")} style={{ width: size, height: size, borderRadius: r, fontSize: Math.round(size * 0.46) }}>{initials(name).slice(0, 1)}</span>;
}

/**
 * Application shell (§1.3, §5). `variant` picks the look: admin (tenant brand sidebar), hosting (fixed provider
 * look: ink sidebar + accent) and portal (top bar on desktop, bottom tab bar on phone, touch sizes).
 * Slots: `environment` badge, `search`, `strip` (e.g. "Viewing Acme A/S · read-only"), `topRight`, `userMenu`.
 */
export function AppShell({ brand, name, logoUrl, sections, user, onLogout, children, topRight, variant = "admin", environment, search, strip, viewOnly, bottomItems, userMenu }: {
  brand: ReactNode; name?: string; logoUrl?: string | null; sections: NavSection[]; user?: { displayName: string; subtitle?: string }; onLogout?: () => void; children: ReactNode; topRight?: ReactNode;
  variant?: ShellVariant; environment?: ReactNode; search?: ReactNode; strip?: ReactNode; viewOnly?: boolean;
  /** Replaces the default user block in the top bar (e.g. `<AvatarMenu />`). */ userMenu?: ReactNode;
  /** Portal phone tab bar: the first 4 items are tabs, the rest live under "More". Defaults to the first section's items. */
  bottomItems?: NavItem[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const hosting = variant === "hosting";
  const displayName = name ?? (typeof brand === "string" ? brand : "");

  if (variant === "portal") {
    const items = sections.flatMap((s) => s.items);
    const tabs = (bottomItems ?? items).slice(0, 4);
    const more = (bottomItems ?? items).slice(4);
    return (
      <div className="iga-portal flex min-h-full flex-col">
        <header className="sticky top-0 z-30 border-b border-line bg-surface">
          <div className="mx-auto flex h-14 max-w-[1280px] items-center gap-4 px-4">
            <div className="flex items-center gap-2.5 font-semibold"><LogoTile name={displayName} logoUrl={logoUrl} />{brand}</div>
            <nav className="hidden flex-1 items-center gap-1 md:flex" aria-label="Main">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => clsx("inline-flex min-h-11 items-center gap-2 rounded-control px-3 font-medium", isActive ? "bg-brand-soft text-brand-text" : "text-fg-2 hover:bg-surface-2 hover:text-fg")}>
                  {({ isActive }) => <><span aria-current={isActive ? "page" : undefined}>{it.label}</span>{it.badge ? <span className={clsx("inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold", it.badgeTone === "warn" ? "iga-tone-warn" : "bg-brand text-on-brand")}>{it.badge}</span> : null}</>}
                </NavLink>
              ))}
            </nav>
            <div className="ml-auto flex items-center gap-2">
              {search}{topRight}{userMenu}
              {user && !userMenu && (
                <div className="flex items-center gap-2">
                  <span aria-hidden="true" className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft text-xs font-bold text-brand-text">{initials(user.displayName)}</span>
                  <span className="hidden text-small md:block"><span className="block font-semibold text-fg">{user.displayName}</span>{user.subtitle && <span className="block text-fg-3">{user.subtitle}</span>}</span>
                  {onLogout && <button type="button" className="iga-btn-ghost control-touch px-2" onClick={onLogout} aria-label={t("ui.signOut")}><LogOut className="h-4 w-4" aria-hidden="true" /></button>}
                </div>
              )}
            </div>
          </div>
          {strip}
        </header>
        <main className="mx-auto w-full max-w-[1280px] flex-1 p-4 pb-24 sm:p-6 md:pb-6">{children}</main>
        <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] md:hidden" aria-label="Main">
          <ul className="flex">
            {tabs.map((it) => (
              <li key={it.to} className="flex-1">
                <NavLink to={it.to} end={it.end} className={({ isActive }) => clsx("flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs font-medium", isActive ? "text-brand-text" : "text-fg-2")}>
                  <span className="relative">{it.icon}{it.badge ? <span className="absolute -right-2 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-on-brand">{it.badge}</span> : null}</span>
                  {it.label}
                </NavLink>
              </li>
            ))}
            {more.length > 0 && (
              <li className="flex-1">
                <button type="button" className="flex min-h-14 w-full flex-col items-center justify-center gap-0.5 text-xs font-medium text-fg-2" onClick={() => setOpen(true)}><MoreHorizontal className="h-4 w-4" aria-hidden="true" />More</button>
              </li>
            )}
          </ul>
        </nav>
        {open && (
          <div className="fixed inset-0 z-40 bg-[rgb(20_28_38/.45)] md:hidden" onClick={() => setOpen(false)}>
            <div className="absolute inset-x-0 bottom-0 rounded-t-dialog bg-surface p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-e3" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="More">
              <div className="mb-2 flex items-center justify-between"><span className="text-h3">More</span><button type="button" className="iga-btn-ghost control-touch px-2" onClick={() => setOpen(false)} aria-label={t("ui.close")}><X className="h-4 w-4" aria-hidden="true" /></button></div>
              {more.map((it) => <NavLink key={it.to} to={it.to} end={it.end} className="flex min-h-11 items-center gap-3 rounded-control px-2 font-medium text-fg hover:bg-surface-2" onClick={() => setOpen(false)}>{it.icon}{it.label}</NavLink>)}
              {onLogout && <button type="button" className="flex min-h-11 w-full items-center gap-3 rounded-control px-2 text-left font-medium text-fg hover:bg-surface-2" onClick={onLogout}><LogOut className="h-4 w-4" aria-hidden="true" />{t("ui.signOut")}</button>}
            </div>
          </div>
        )}
      </div>
    );
  }

  const sidebarClass = hosting ? "iga-nav-hosting bg-provider-sidebar text-provider-sidebar-fg" : "border-r border-line bg-surface";
  return (
    <div className="flex h-full">
      <aside className={clsx("fixed inset-y-0 left-0 z-40 flex w-[232px] transform flex-col transition-transform lg:static lg:translate-x-0", sidebarClass, open ? "translate-x-0" : "-translate-x-full")} aria-label="Sidebar">
        <div className="flex items-center gap-2.5 px-4 pb-2 pt-4">
          <LogoTile name={displayName} logoUrl={logoUrl} hosting={hosting} />
          <div className={clsx("min-w-0 flex-1 truncate font-bold", hosting && "text-white")}>{brand}</div>
          {environment && <span className={clsx("inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold", hosting ? "bg-provider-sidebar-active text-white" : "iga-tone-neutral")}>{environment}</span>}
          <button type="button" className="iga-btn-ghost control-sm px-2 lg:hidden" onClick={() => setOpen(false)} aria-label={t("ui.close")}><X className="h-4 w-4" aria-hidden="true" /></button>
        </div>
        {search && <div className="px-3 pb-2">{search}</div>}
        <nav className="flex flex-1 flex-col overflow-y-auto px-2.5 pb-3" aria-label="Main">
          {sections.map((s, i) => (
            <div key={i} className={clsx(s.title ? "mt-2" : "mt-1")}>
              {s.title && <div className={clsx("px-2.5 pb-1 pt-3 text-[11.5px] font-semibold uppercase tracking-[0.05em]", hosting ? "text-provider-sidebar-fg/70" : "text-fg-3")}>{s.title}</div>}
              {s.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => clsx("iga-nav-link", isActive && "active")} onClick={() => setOpen(false)}>
                  {({ isActive }) => <><span aria-current={isActive ? "page" : undefined} className="contents">{it.icon}<span>{it.label}</span></span><CountBadge n={it.badge} tone={it.badgeTone} dark={hosting} /></>}
                </NavLink>
              ))}
            </div>
          ))}
          {user && (
            <div className={clsx("mt-auto flex items-center gap-2.5 rounded-control border p-2 pt-2", hosting ? "border-provider-sidebar-active" : "border-line")}>
              <span aria-hidden="true" className={clsx("flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-bold", hosting ? "bg-provider-accent text-white" : "bg-brand-soft text-brand-text")}>{initials(user.displayName)}</span>
              <div className="min-w-0 flex-1">
                <div className={clsx("truncate font-semibold", hosting && "text-white")}>{user.displayName}</div>
                {user.subtitle && <div className={clsx("truncate text-xs", hosting ? "text-provider-sidebar-fg/80" : "text-fg-3")}>{user.subtitle}</div>}
              </div>
              {onLogout && <button type="button" className={clsx("iga-btn-ghost control-sm px-2", hosting && "text-provider-sidebar-fg hover:bg-provider-sidebar-active hover:text-white")} onClick={onLogout} aria-label={t("ui.signOut")}><LogOut className="h-4 w-4" aria-hidden="true" /></button>}
            </div>
          )}
        </nav>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-[rgb(20_28_38/.45)] lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-line bg-surface px-4 lg:hidden">
          <button type="button" className="iga-btn-ghost control-sm px-2" onClick={() => setOpen(true)} aria-label={t("ui.openMenu")}><Menu className="h-5 w-5" aria-hidden="true" /></button>
          <div className="font-semibold">{brand}</div>
          <div className="ml-auto">{topRight}</div>
        </header>
        {topRight && <div className="hidden h-12 items-center justify-end gap-2 border-b border-line bg-surface px-6 lg:flex">{viewOnly && <ViewOnly />}{topRight}</div>}
        {strip}
        <main className="flex-1 overflow-y-auto p-4 sm:p-7">{children}</main>
      </div>
    </div>
  );
}

/** Persistent context strip, e.g. "Viewing Acme A/S · read-only" in the tenant's brand colour (hosting). */
/** Brand-coloured strip under the top bar ("Viewing Acme · read-only"). With `color` the on-brand text colour is computed (white or ink, whichever contrasts more). */
export function ContextStrip({ children, color, className }: { children: ReactNode; color?: string; className?: string }) {
  const onBrand = color ? computeBrand(color).onBrand : undefined;
  return <div className={clsx("flex min-h-9 items-center gap-2 px-4 text-small font-semibold", !color && "text-on-brand", className)} style={{ background: color ?? "var(--brand)", color: onBrand }} role="status">{children}</div>;
}

/**
 * Page header (§3): breadcrumb (level 2+), title, one-sentence subtitle, status badge next to the title,
 * actions top-right with at most one primary. `viewOnly` adds the quiet label for read-only access.
 */
export function PageHeader({ title, subtitle, actions, breadcrumb, status, viewOnly, portal, className }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; breadcrumb?: ReactNode; status?: ReactNode; viewOnly?: boolean; portal?: boolean; className?: string }) {
  return (
    <div className={clsx("mb-5 flex flex-wrap items-start justify-between gap-3", className)}>
      <div className="flex min-w-0 flex-col gap-1">
        {breadcrumb && <div className="text-[12.5px] text-fg-3">{breadcrumb}</div>}
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className={portal ? "text-h1-portal" : "text-h1"}>{title}</h1>
          {status}
          {viewOnly && <ViewOnly />}
        </div>
        {subtitle && <p className="text-fg-3">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>
  );
}

/** Breadcrumb from level 2: "Integrations › Systems". Last item is the current page's parent, not the page. */
export function Breadcrumb({ items: raw }: { items: { label: ReactNode; href?: string; to?: string }[] }) {
  // Adjacent crumbs with the same label (a nav group and its first page, "Identities › Identities") collapse into one; the linked crumb wins.
  const items = raw.reduce<typeof raw>((acc, it) => {
    const prev = acc[acc.length - 1];
    if (prev && prev.label === it.label) { if (it.to || it.href || !(prev.to || prev.href)) acc[acc.length - 1] = it; return acc; }
    acc.push(it); return acc;
  }, []);
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && <span aria-hidden="true">›</span>}
            {it.to ? <NavLink to={it.to} className="text-fg-3 hover:text-fg hover:underline">{it.label}</NavLink> : it.href ? <a href={it.href} className="text-fg-3 hover:text-fg hover:underline">{it.label}</a> : <span>{it.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}
